import { useState } from "react";
import {
    Alert,
    Button,
    Chip,
    Divider,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    TextField,
    Tooltip
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { setIntegrationSignedIdentity } from "../../../../api/admin/integrations/endpoints-referrers";
import type {
    EmbedHandoffMethod,
    Integration,
    SetSignedIdentityRequest
} from "../../../../common/interfaces/admin/integration.interface";

export interface IntegrationSignedIdentityConfigProps {
    companyId: number;
    integration: Integration;
    onUpdated(updated: Integration): void;
}

/** Cryptographically-strong base64url secret for the admin to share with the signer. */
function generateSecret(): string {
    const bytes = new Uint8Array(32);
    window.crypto.getRandomValues(bytes);
    let bin = "";
    bytes.forEach(b => (bin += String.fromCharCode(b)));
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function CopyBlock({ label, code }: { label: string; code: string }) {
    const [copied, setCopied] = useState(false);
    return (
        <div className="integration-signed__code">
            <div className="integration-signed__code-head">
                <span>{label}</span>
                <Tooltip title={copied ? "Copied" : "Copy"}>
                    <IconButton
                        size="small"
                        aria-label={`Copy ${label}`}
                        onClick={() => {
                            void navigator.clipboard?.writeText(code);
                            setCopied(true);
                            window.setTimeout(() => setCopied(false), 1500);
                        }}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </div>
            <pre>
                <code>{code}</code>
            </pre>
        </div>
    );
}

function hmacSample(integrationId: string): string {
    return `// Your backend (Node) — signs an identity hand-off.
// The HMAC secret is the one you saved here; never expose it to the browser.
import crypto from "node:crypto";
const SECRET = process.env.MBASSIST_HMAC_SECRET;

export function signMbassistIdentity({ email, name, externalId }) {
  const payload = {
    email, name, externalId,
    integrationId: "${integrationId}",
    createdAt: new Date().toISOString(),     // must be < 5 minutes old
  };
  // Sign over the EXACT base64url(JSON) bytes — this is the contract.
  const data = Buffer.from(JSON.stringify(payload)).toString("base64url");
  const sig = crypto.createHmac("sha256", SECRET).update(data).digest("base64url");
  return { handoffMethod: "hmac", data, sig };
}`;
}

function jwtSample(integrationId: string, iss: string, aud: string): string {
    return `// Your backend issues a short-lived RS256 JWT and serves its JWKS at the URL
// you configured here. Claims iss/aud must match exactly; keep exp short.
import { SignJWT } from "jose";

export async function signMbassistIdentity(privateKey, { email, name, externalId }) {
  const token = await new SignJWT({ email, name, externalId, integrationId: "${integrationId}" })
    .setProtectedHeader({ alg: "RS256", kid: "<your-signing-kid>" })
    .setIssuer("${iss || "<your issuer>"}")
    .setAudience("${aud || "<your audience>"}")
    .setIssuedAt()
    .setExpirationTime("3m")
    .sign(privateKey);
  return { handoffMethod: "jwt", token };
}`;
}

function hostSample(integrationId: string, scriptUrl: string): string {
    return `<!-- Your page: hand the signed identity to the widget. -->
<script>
  window.MBAssist = window.MBAssist || {};
  // Called at handshake and on each refresh request. Ask YOUR backend for a
  // freshly signed payload (it returns whatever signMbassistIdentity produced).
  window.MBAssist.getSignedIdentity = async () => {
    const res = await fetch("/your-app/mbassist-identity", { credentials: "include" });
    return res.json();
  };
</script>
<script async src="${scriptUrl}" data-integration-id="${integrationId}"></script>`;
}

export default function IntegrationSignedIdentityConfig({
    companyId,
    integration,
    onUpdated
}: IntegrationSignedIdentityConfigProps) {
    const [method, setMethod] = useState<EmbedHandoffMethod>(integration.handoffMethod ?? "hmac");
    const [hmacSecret, setHmacSecret] = useState("");
    const [expectedIssuer, setExpectedIssuer] = useState(integration.expectedIssuer ?? "");
    const [expectedAudience, setExpectedAudience] = useState(integration.expectedAudience ?? "");
    const [jwksUri, setJwksUri] = useState("");
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const isHmac = method === "hmac";
    const canSave = isHmac
        ? hmacSecret.trim().length > 0
        : expectedIssuer.trim().length > 0 &&
          expectedAudience.trim().length > 0 &&
          jwksUri.trim().length > 0;

    const save = async () => {
        setSaving(true);
        setFormError(null);
        try {
            const body: SetSignedIdentityRequest = isHmac
                ? { handoffMethod: "hmac", hmacSecret: hmacSecret.trim() }
                : {
                      handoffMethod: "jwt",
                      expectedIssuer: expectedIssuer.trim(),
                      expectedAudience: expectedAudience.trim(),
                      jwksUri: jwksUri.trim()
                  };
            const { data } = await setIntegrationSignedIdentity(companyId, integration.id, body);
            onUpdated(data as Integration);
            setHmacSecret(""); // write-only — it's in Secrets Manager now
        } catch (err: any) {
            setFormError(err?.response?.data?.message ?? "Saving the configuration failed.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="integration-signed">
            <section className="integration-signed__section">
                <div className="integration-signed__status">
                    {integration.signedIdentityConfigured ? (
                        <Chip
                            color="success"
                            label={`Configured (${integration.handoffMethod ?? "?"})`}
                        />
                    ) : (
                        <Chip label="Not configured" />
                    )}
                </div>
                <Alert severity="info">
                    Your backend signs the visitor identity; the widget relays it and MBAssist
                    verifies it. Secrets are stored in AWS Secrets Manager and never returned — save
                    them somewhere safe.
                </Alert>

                <RadioGroup
                    row
                    value={method}
                    onChange={e => setMethod(e.target.value as EmbedHandoffMethod)}
                >
                    <FormControlLabel
                        value="hmac"
                        control={<Radio />}
                        label="HMAC (shared secret)"
                    />
                    <FormControlLabel value="jwt" control={<Radio />} label="JWT (your JWKS)" />
                </RadioGroup>

                {formError && <Alert severity="error">{formError}</Alert>}

                {isHmac ? (
                    <>
                        <Alert severity="warning">
                            Copy this secret now and give it to the team running your signer — it
                            cannot be retrieved after saving.
                        </Alert>
                        <div className="integration-signed__secret-row">
                            <TextField
                                label="Shared HMAC secret"
                                value={hmacSecret}
                                onChange={e => setHmacSecret(e.target.value)}
                                fullWidth
                                type="text"
                            />
                            <Button onClick={() => setHmacSecret(generateSecret())}>
                                Generate
                            </Button>
                            <Tooltip title="Copy secret">
                                <span>
                                    <IconButton
                                        aria-label="Copy secret"
                                        disabled={!hmacSecret}
                                        onClick={() =>
                                            void navigator.clipboard?.writeText(hmacSecret)
                                        }
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </div>
                    </>
                ) : (
                    <>
                        <TextField
                            label="Expected issuer (iss)"
                            value={expectedIssuer}
                            onChange={e => setExpectedIssuer(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Expected audience (aud)"
                            value={expectedAudience}
                            onChange={e => setExpectedAudience(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="JWKS URL (reachable from the MBAssist backend)"
                            value={jwksUri}
                            onChange={e => setJwksUri(e.target.value)}
                            placeholder="https://your-idp.example.com/.well-known/jwks.json"
                            helperText="Where MBAssist fetches your public signing keys."
                            fullWidth
                        />
                    </>
                )}

                <Button variant="contained" onClick={save} disabled={saving || !canSave}>
                    {saving
                        ? "Saving…"
                        : integration.signedIdentityConfigured
                          ? "Update configuration"
                          : "Save configuration"}
                </Button>
            </section>

            <Divider />

            {/* --- Customer integration sample --------------------------------- */}
            <section className="integration-signed__section">
                <h3>Customer integration code</h3>
                <CopyBlock
                    label={isHmac ? "Signer (HMAC)" : "Signer (JWT)"}
                    code={
                        isHmac
                            ? hmacSample(integration.id)
                            : jwtSample(integration.id, expectedIssuer, expectedAudience)
                    }
                />
                <CopyBlock
                    label="Host page"
                    code={hostSample(integration.id, integration.scriptUrl)}
                />
            </section>
        </div>
    );
}
